/**
 * LocationPicker
 *
 * Searchable Address Book selector for the Task Creation form.
 *
 * Props:
 *   label       {string}       — section label, e.g. "Pickup Location"
 *   selected    {object|null}  — currently selected location record (null = none)
 *   onSelect    {function}     — called with the full location object when one is chosen
 *   onClear     {function}     — called when the X button is pressed on the chip
 *   placeholder {string}       — input placeholder text
 *
 * Behavior:
 *   - While nothing is selected, shows a search input backed by GET /locations/
 *   - When query is empty, shows up to 5 recent locations from localStorage
 *   - When query is non-empty, debounces a backend search (300 ms)
 *   - Keyboard: arrows move highlight, Enter selects, Escape closes
 *   - When a location is selected, shows a compact chip with a clear [×] button
 *   - Clearing does NOT erase the form fields that were populated — dispatch
 *     can use the saved location as a starting point and then customise
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { listLocations } from "../../api/locations";

const RECENTS_KEY = "ci_recent_locations";
const MAX_RECENTS  = 5;
const SEARCH_LIMIT = 20;

// ── Type-color map (matches AddressBook.jsx semantics exactly) ────────────────
const TYPE_STYLE = {
  hospital:       { bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" },
  surgery_center: { bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" },
  vendor:         { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
  customer:       { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  warehouse:      { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  other:          { bg: "#f8fafc", text: "#94a3b8", border: "#e2e8f0" },
};

const TYPE_LABEL = {
  vendor:         "Vendor",
  hospital:       "Hospital",
  surgery_center: "Surgery Center",
  warehouse:      "Warehouse",
  customer:       "Customer",
  other:          "Other",
};

function typeStyle(type) {
  return TYPE_STYLE[type] ?? TYPE_STYLE.other;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TypePill({ type }) {
  const s = typeStyle(type);
  return (
    <span style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 8,
      fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.05em",
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      whiteSpace: "nowrap",
    }}>
      {TYPE_LABEL[type] ?? type}
    </span>
  );
}

function OptionRow({ loc, highlighted, recent, onMouseDown }) {
  const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
  return (
    <div
      onMouseDown={onMouseDown}
      style={{
        padding: "9px 14px",
        cursor: "pointer",
        background: highlighted ? "#f0f9ff" : "transparent",
        borderBottom: "1px solid #f1f5f9",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
      }}
    >
      {/* Left: icon */}
      <span style={{
        fontSize: 16, color: "#94a3b8", marginTop: 1, flexShrink: 0,
        fontFamily: "Material Icons", fontStyle: "normal",
      }}>
        {recent ? "history" : "location_on"}
      </span>

      {/* Right: name + meta */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {loc.name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
          <TypePill type={loc.location_type} />
          {cityState && (
            <span style={{ fontSize: 11, color: "#94a3b8" }}>{cityState}</span>
          )}
          {loc.address_line_1 && (
            <span style={{ fontSize: 11, color: "#cbd5e1" }}>{loc.address_line_1}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Status badge for archived / missing locations ─────────────────────────────
// The _status field is added by the re-hydration effect in task-create when the
// background live-record check reveals the linked location is no longer active.
// It is never persisted to the backend — it only drives this UI hint.

const STATUS_DISPLAY = {
  archived:    { label: "Archived",    bg: "#fef3c7", text: "#b45309", border: "#fcd34d", icon: "archive" },
  not_found:   { label: "Not Found",   bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5", icon: "help_outline" },
  unavailable: { label: "Unavailable", bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1", icon: "cloud_off" },
};

function StatusPill({ status }) {
  const d = STATUS_DISPLAY[status];
  if (!d) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "1px 7px", borderRadius: 8,
      fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.04em",
      background: d.bg, color: d.text, border: `1px solid ${d.border}`,
      whiteSpace: "nowrap",
    }}>
      <span style={{ fontFamily: "Material Icons", fontStyle: "normal", fontSize: 10 }}>{d.icon}</span>
      {d.label}
    </span>
  );
}

// ── Selected state chip ───────────────────────────────────────────────────────

function SelectedChip({ location, label, onClear }) {
  const cityState  = [location.city, location.state].filter(Boolean).join(", ");
  const locStatus  = location._status; // "archived" | "not_found" | "unavailable" | undefined
  const hasWarning = !!locStatus;

  // Chip border/background shifts to amber when there is a status warning
  const chipStyle = hasWarning
    ? { background: "#fffbeb", border: "1px solid #fcd34d" }
    : { background: "#f0fdf4", border: "1px solid #86efac" };

  const iconColor  = hasWarning ? "#b45309"  : "#16a34a";
  const iconName   = hasWarning ? "warning"  : "check_circle";

  return (
    <div>
      <div style={{
        fontSize: "0.65rem", fontWeight: 700, color: "#64748b",
        letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 5,
      }}>
        {label}
      </div>
      <div style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        padding: "8px 12px",
        borderRadius: 10,
        ...chipStyle,
      }}>
        <span style={{
          fontFamily: "Material Icons", fontStyle: "normal",
          fontSize: 18, color: iconColor, flexShrink: 0, marginTop: 1,
        }}>
          {iconName}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", lineHeight: 1.3 }}>
            {location.name || "(Unnamed location)"}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
            {location.location_type && <TypePill type={location.location_type} />}
            {cityState && <span style={{ fontSize: 11, color: "#64748b" }}>{cityState}</span>}
            {/* Status badge — shown only for archived / missing / unavailable records */}
            {hasWarning && <StatusPill status={locStatus} />}
          </div>

          {(location.contact_name || location.contact_phone) && (
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
              {[location.contact_name, location.contact_phone].filter(Boolean).join(" · ")}
            </div>
          )}
          {location.receiving_hours && (
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
              ⏰ {location.receiving_hours}
            </div>
          )}

          {/* Non-blocking guidance for dispatchers when the location is unavailable */}
          {hasWarning && (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4, fontStyle: "italic" }}>
              Snapshot preserved · Clear to select a replacement or enter manually
            </div>
          )}
        </div>

        <Tooltip
          title={hasWarning
            ? "Clear link — task address fields will be preserved"
            : "Clear selection — address fields will be preserved"}
          arrow
        >
          <button
            type="button"
            onClick={onClear}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "4px", borderRadius: "50%", display: "flex",
              alignItems: "center", color: "#94a3b8", flexShrink: 0,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#94a3b8"; }}
          >
            <span style={{ fontFamily: "Material Icons", fontStyle: "normal", fontSize: 18, display: "block" }}>
              close
            </span>
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LocationPicker({ label, selected, onSelect, onClear, placeholder }) {
  const [query,       setQuery]       = useState("");
  const [options,     setOptions]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [open,        setOpen]        = useState(false);
  const [highlight,   setHighlight]   = useState(-1);
  const [recents,     setRecents]     = useState([]);
  const [fetchError,  setFetchError]  = useState(false);

  const wrapperRef = useRef(null);
  const inputRef   = useRef(null);

  // ── Load recent locations ─────────────────────────────────────────────────

  const loadRecents = useCallback(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      setRecents(JSON.parse(raw || "[]").slice(0, MAX_RECENTS));
    } catch {
      setRecents([]);
    }
  }, []);

  useEffect(() => { loadRecents(); }, [loadRecents]);

  // ── Backend search ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;

    if (!query.trim()) {
      setOptions(recents);
      setHighlight(-1);
      setLoading(false);
      setFetchError(false);
      return;
    }

    setLoading(true);
    setFetchError(false);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res  = await listLocations({ q: query.trim(), active: true, limit: SEARCH_LIMIT });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json().catch(() => null);
          setOptions(data?.items ?? []);
        } else {
          setOptions([]);
        }
      } catch {
        if (!cancelled) { setFetchError(true); setOptions([]); }
      } finally {
        if (!cancelled) { setLoading(false); setHighlight(-1); }
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open, recents]);

  // ── Click outside → close ─────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // ── Select handler ────────────────────────────────────────────────────────

  const handleSelect = useCallback((loc) => {
    // Save to recent locations
    try {
      const existing = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
      const updated  = [loc, ...existing.filter((r) => r.id !== loc.id)].slice(0, MAX_RECENTS);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
    } catch {}

    setQuery("");
    setOpen(false);
    setOptions([]);
    onSelect(loc);
  }, [onSelect]);

  // ── Keyboard navigation ───────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && options[highlight]) handleSelect(options[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // ── When a location is already selected, show the chip ───────────────────

  if (selected) {
    return <SelectedChip location={selected} label={label} onClear={onClear} />;
  }

  // ── Search input + dropdown ───────────────────────────────────────────────

  const showDropdown = open && (options.length > 0 || loading || fetchError || query.trim());

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{
        fontSize: "0.65rem", fontWeight: 700, color: "#64748b",
        letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 5,
      }}>
        {label}
      </div>
      <div style={{
        display: "flex", alignItems: "center",
        border: "1px solid #d2d6da", borderRadius: 8, background: "#fff",
        padding: "0 10px",
        boxShadow: open ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
        transition: "box-shadow 0.15s",
      }}>
        <span style={{
          fontFamily: "Material Icons", fontStyle: "normal",
          fontSize: 16, color: "#94a3b8", marginRight: 7, flexShrink: 0,
        }}>
          search
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder || "Search Address Book by name, city, or type…"}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          style={{
            border: "none", outline: "none", flex: 1,
            fontSize: 13, padding: "10px 0", background: "transparent",
            color: "#1e293b",
          }}
          autoComplete="off"
        />
        {loading && (
          <CircularProgress size={14} sx={{ color: "#94a3b8", flexShrink: 0 }} />
        )}
        {query && !loading && (
          <button
            type="button"
            onClick={() => { setQuery(""); setOpen(true); inputRef.current?.focus(); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "2px", display: "flex", alignItems: "center", color: "#94a3b8",
            }}
          >
            <span style={{ fontFamily: "Material Icons", fontStyle: "normal", fontSize: 16 }}>close</span>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
          zIndex: 9999,
          maxHeight: 280,
          overflowY: "auto",
        }}>
          {!query.trim() && recents.length > 0 && (
            <div style={{
              padding: "7px 14px 4px",
              fontSize: "0.6rem", fontWeight: 700, color: "#94a3b8",
              letterSpacing: "0.07em", textTransform: "uppercase",
            }}>
              Recent
            </div>
          )}

          {fetchError ? (
            <div style={{ padding: "14px 16px", fontSize: 13, color: "#ef4444" }}>
              Could not reach Address Book. Check your connection.
            </div>
          ) : loading ? (
            <div style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8", display: "flex", gap: 8, alignItems: "center" }}>
              <CircularProgress size={14} /> Searching…
            </div>
          ) : options.length === 0 ? (
            <div style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8" }}>
              {query.trim() ? "No locations found." : "No recent locations."}
            </div>
          ) : (
            options.map((loc, i) => (
              <OptionRow
                key={loc.id}
                loc={loc}
                highlighted={highlight === i}
                recent={!query.trim()}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevents blur before click registers
                  handleSelect(loc);
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
